import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogContent,
  Switch,
  TableSortLabel,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import NavBar from "../NavBar";
import api from "../../axiosInstance";
import { useTheme } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { toast, ToastContainer } from "react-toastify";
import BannerModal from "./BannerModal";
import CloseIcon from "@mui/icons-material/Close";
import MergeIcon from "@mui/icons-material/Merge";
import BannerCourseMapping from "./BannerCourseMapping";
import AdvertisementsModal from "./AdvertisementsModal";
import AdvertisementsMapping from "./AdvertisementsMapping";
import ViewListIcon from "@mui/icons-material/ViewList";

// Column config and visibility state
const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.id,
  },
  {
    key: "adId",
    label: "Ad Id",
    cellCss: { align: "center", minWidth: "50px" },
    getValue: (r) => r.adId,
  },
  {
    key: "name",
    label: "Name",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.name,
  },
  {
    key: "image",
    label: "Image",
    getValue: (r) => r.image,
  },
  {
    key: "link",
    label: "Link",
    getValue: (r) => r.link,
  },
  {
    key: "courses",
    label: "Courses",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.courses?.map((c) => c.courseName).join(", "),
  },
  {
    key: "screens",
    label: "Screens",
    cellCss: { align: "center", minWidth: "100px" },
    getValue: (r) => r.screens?.map((c) => c.screenName).join(", "),
  },
  {
    key: "priority",
    label: "Priority",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.priority,
  },
  {
    key: "active",
    label: "Active",
    getValue: (r) => (r.active ? "Yes" : "No"),
  },
];
const defaultVisibleKeys = new Set([
  "name",
  "stateName",
  "image",
  "courses",
  "screens",
  "priority",
  "link",
  "active",
]);

const STORAGE_KEY = "AdvertisementsVisibleColumns";

function Advertisements() {
  const theme = useTheme();
  const navigation = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [advertisements, setAdvertisements] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openMap, setOpenMap] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const handleadvertisementsMapClose = () => setOpenMap(false);
  const [selectedstates, setSelectedStates] = useState("1" || "");
  const [searchQuery, setSearchQuery] = useState(() => {
    return localStorage.getItem("Advertisements_management") || "";
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [advertisementsEdit, setAdvertisementsEdit] = useState(null);
  const [advertisementsMap, setAdvertisementsMap] = useState(null);
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  // Save to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("Advertisements_management", searchQuery);
  }, [searchQuery]);
  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };
  const filteredData = advertisements.filter((ad) =>
    ad.name?.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const sortedData = useMemo(() => {
    if (!filteredData) return [];
    return [...filteredData].sort((a, b) => {
      if (!orderBy) return 0;

      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (typeof aValue === "boolean") {
        return order === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      return 0;
    });
  }, [filteredData, orderBy, order]);

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };
  const handleChange = (event) => {
    setSelectedStates(event.target.value);
  };

  //Fetching the advertisements data with states id
  useEffect(() => {
    setIsLoading(true);
    api
      .get(`/api/v1/advertisements`)
      .then((res) => setAdvertisements(res.data.data))
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          err.message ||
          "Something went wrong while fetching messages";
        setError(message);
      })
      .finally(() => setIsLoading(false));
  }, [selectedstates]);

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    const normalized = value === true || value === "true" || value === "Yes";
    return (
      <Chip
        label={normalized ? "Yes" : "No"}
        color={normalized ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get content type chip styling
  const getContentTypeChip = (type) => {
    const typeConfig = {
      text: { color: "primary", label: "Text" },
      image: { color: "secondary", label: "Image" },
      video: { color: "success", label: "Video" },
      link: { color: "info", label: "Link" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // page nation rows handles
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  // pagenation code
  const paginatedAdvertisement = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );
  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Delete The Advertisement record api fetch data
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/advertisements/${selectedId}`);

      // If deletion is successful, update local state update
      setAdvertisements((prev) => prev.filter((row) => row.id !== selectedId));
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  // opening Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setAdvertisementsEdit([]);
  };
  const handleadvertisementsMapOpen = () => {
    setOpenMap(true);
    setAdvertisementsMap([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setAdvertisementsEdit(rowData);
  };
  const handleMapClick = (rowData) => {
    handleadvertisementsMapOpen();
    setAdvertisementsMap(rowData);
  };
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    // update locally
    setAdvertisements((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      // If editing: replace the item
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        // If adding: push new item
        return [newItem, ...prev];
      }
    });

    setAdvertisementsEdit(null);
  };
  return (
    <>
      {/* <NavBar /> */}
      <ToastContainer />
      {/* <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: { xs: "flex-start", md: "space-between" },
          alignItems: { xs: "stretch", md: "center" },
          gap: { xs: 1, md: 0 },
          mb: 2,
          marginLeft: "10px",
          marginTop: "10px",
        }}
      >
        <Box></Box>
         <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <Link underline="hover" color="inherit" to="/booklist">
            Books List
          </Link>
          <Typography sx={{ color: "text.primary", fontWeight: 600 }}>
            Banner Management
          </Typography>
        </Breadcrumbs> 

        <Box>
          
        </Box>
      </Box> */}
      {/* Search Section */}
      <Card sx={{ m: { xs: 1, md: "0 15px" }, boxShadow: 1 }}>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            gap: 2,
            mr: { md: "40px" },
          }}
        >
          {/* Search Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: { xs: "100%", md: "60%" },
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                maxWidth: { md: "60%", xs: "100%" },
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                textAlign: { xs: "left" },
                width: "100%",
              }}
            >
              {sortedData.length} of {advertisements.length} advertisements
              items
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              width: { xs: "100%", md: "55%" },
            }}
          >
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: { xs: "wrap", md: "nowrap" },
                mt: { xs: 1, md: 0 },
                marginRight: "10px",
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<ViewListIcon />}
                onClick={() => navigation("/advertisement-course-mapping")}
                {...BUTTON_STYLES.primary}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                View Advertisement Courses Map
              </Button>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                ssx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{
                  sx: {
                    width: 200,
                    maxHeight: 500,
                    overflowY: "auto",
                    borderRadius: 2,
                  },
                }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={
                      <Checkbox
                        checked={visibleColumns.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                    }
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleNewOpen}
                {...BUTTON_STYLES.primary}
                sx={{
                  minWidth: { xs: "140px", md: "185px" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Add Advertisement
              </Button>
            </Stack>
            <Tooltip title="Dense padding" arrow>
              <FormControlLabel
                control={
                  <Switch
                    checked={dense}
                    onChange={handleChangeDense}
                    color="primary"
                  />
                }
                sx={{
                  width: { xs: "20%", sm: "auto" },
                  justifyContent: "center",
                  mx: 0,
                }}
              />
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
            }}
          >
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px ",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          ...c.cellCss,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(paginatedAdvertisement) &&
                  paginatedAdvertisement.length > 0 ? (
                    paginatedAdvertisement.map((row) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        {visibleColumnDefs.map((c) => (
                          <TableCell
                            key={c.key}
                            sx={{ padding: "10px 10px", ...c.cellCss }}
                          >
                            {(() => {
                              const value = c.getValue(row);
                              const key = c.key;

                              // Optional debug

                              if (key === "active" || key === "isNational") {
                                return getBooleanChip(value);
                              }

                              if (key === "contentType") {
                                return getContentTypeChip(value);
                              }

                              if (key === "image") {
                                return key === "image" ? (
                                  <img
                                    src={`https://siaedutech.com/public/${value}`}
                                    alt="preview"
                                    style={{
                                      width: "60px",
                                      height: "auto",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      setOpenImage(
                                        `https://siaedutech.com/public/${value}`,
                                      )
                                    }
                                  />
                                ) : (
                                  value // show fallback text if not valid image
                                );
                              }

                              return value;
                            })()}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            align: "center",
                            padding: "10px 10px",
                            minWidth: "120px",
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip
                              title="Edit Advertisement"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => handleEditClick(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Delete Advertisement"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="error"
                                onClick={() => handleClickOpen(row.id)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "error.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Advertisement Course Mapping"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="#0dec84"
                                onClick={() => handleMapClick(row)}
                                size="small"
                                sx={{
                                  color: "#0b6a3e",
                                  "&:hover": {
                                    backgroundColor: "#0b6a3e",
                                    color: "white",
                                  },
                                }}
                              >
                                <MergeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {searchQuery
                              ? "No advertisements found matching your search"
                              : "No Bannners available"}
                          </Typography>
                          {searchQuery && (
                            <Button
                              variant="outlined"
                              onClick={() => setSearchQuery("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 20, 30, 50, 100]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}
      {/* Advertisement modal */}
      <AdvertisementsModal
        open={openNew}
        onClose={handleNewClose}
        advertisementsEdit={advertisementsEdit}
        onSuccess={handleFormSuccess}
      />
      <AdvertisementsMapping
        open={openMap}
        onClose={handleadvertisementsMapClose}
        advertisementsMap={advertisementsMap}
        onSuccess={handleFormSuccess}
      />
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth="md"
      >
        <DialogContent>
          <Box position="relative">
            <IconButton
              aria-label="close"
              onClick={() => setOpenImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={openImage}
              alt="Preview"
              style={{ width: "100%", height: "auto", borderRadius: 4 }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Advertisements;
